/**
 * Conversational apply — logo, chat Q&A, Owen insight bar only.
 * Routes questions dynamically via V1 triage/fields or legacy program router.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';
import { RotateCcw, Save } from 'lucide-react';
import {
  updateProductRecommendation,
  setRecommendedProduct,
  setRecommendedProducts,
  setCurrentStep,
  setUser,
  updateApplicationData,
  updateV1TriageAnswers,
  updateV1FieldAnswers,
  setV1Phase,
  setV1ProductId,
  setV1ExitId,
  clearV1Exit,
  restartApplication,
  resetAssessmentProgress,
  recalculateReadiness,
  saveProgress,
  setGuestApplicationId,
  setGuestSaveEmail,
  STEPS,
} from '../../store/loanApplicationSlice';
import {
  formatCreditScoreLabel,
  isValidCreditScore,
} from '../../utils/creditScoreInput';
import { getVisibleRecommendationQuestions } from '../../utils/owenQuestionRouter';
import { getOwenIgnoreLabelForQuestionId, isOwenBlockedQuestionId } from '../../config/owenPrivacy';
import { LK_FEATURES } from '../../config/features';
import {
  getQuestionPlaceholder,
  getQuestionText,
  getQuestionSubtitle,
} from '../../config/recommendationQuestions';
import { getVisibleV1TriageQuestions } from '../../config/v1/triage';
import { getVisibleV1ProductFields } from '../../config/v1/fields';
import { getV1Product, listV1Products } from '../../config/v1/products';
import { getV1Exit } from '../../config/v1/exits';
import { buildLegacyProductRecommendation, buildRecommendedProductFromV1 } from '../../utils/v1/syncV1ToLegacy';
import { calculateRecommendations } from '../../utils/recommendationEngine';
import { getLegacyQuestionById, pickNextLegacyQuestionId, LEGACY_CORE_QUESTION_ORDER } from '../../utils/owenQuestionRouter';
import { deriveLoanSizeTier } from '../../utils/loanSizeTier';
import { formatLocationAnswerLabel, lookupByZip } from '../../utils/propertyLocationLookup';
import { fetchOwenApplyGuidance, fetchOwenApplyWelcome } from '../../utils/owenApplyAI';
import {
  buildLegacyAllowedNextSteps,
  buildV1TriageAllowedNextSteps,
  buildV1FieldAllowedNextSteps,
  defaultStepFromAllowed,
  programsForAi,
} from '../../utils/applyNextSteps';
import { getOptionHelp, getQuestionHelp } from '../../config/optionHelpCatalog';
import { buildOwenSeesFromMessages } from '../../utils/owenSeesLabels';
import { getActiveApplyMilestoneIndex } from '../../config/applyMilestones';
import { shouldMirrorReactionInThread } from '../../config/owenApplyReactions';
import OwenInsightBar from './OwenInsightBar';
import ConversationalQuestionInput from './ConversationalQuestionInput';
import TypewriterText from './TypewriterText';
import TypingIndicator from './TypingIndicator';
import AccountCreationModal from '../Auth/AccountCreationModal';
import V1DocumentPrep from '../LoanApplication/v1/V1DocumentPrep';
import { Button, Modal } from '../../ui/components';
import SaveApplicationModal from './SaveApplicationModal';
import { saveGuestApplication } from '../../utils/saveGuestApplication';
import { ensureFreshApplySession } from '../../utils/applySession';

const StartOverConfirmModal = ({ open, onClose, onConfirm }) => (
  <Modal
    open={open}
    onClose={onClose}
    title="Start over?"
    size="sm"
    footer={(
      <>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="button" variant="danger" onClick={onConfirm}>
          Start over
        </Button>
      </>
    )}
  >
    <p style={{ margin: 0, fontSize: 15, lineHeight: 1.55, color: 'var(--lk-text-secondary)' }}>
      This will reset all of your answers and take you back to the beginning. Are you sure?
    </p>
  </Modal>
);

const formatAnswerLabel = (questionDef, rawValue) => {
  if (!questionDef) return String(rawValue ?? '');
  if (questionDef.type === 'creditScore') {
    return formatCreditScoreLabel(rawValue) || String(rawValue ?? '');
  }
  if (questionDef.type === 'radio' && questionDef.options) {
    const opt = questionDef.options.find((o) => o.value === rawValue);
    return opt?.label || String(rawValue);
  }
  if (questionDef.type === 'currency' && rawValue) {
    const n = parseFloat(String(rawValue).replace(/,/g, ''));
    if (Number.isFinite(n)) return `$${n.toLocaleString()}`;
  }
  if (questionDef.type === 'propertyLocation' && rawValue && typeof rawValue === 'object') {
    return formatLocationAnswerLabel(rawValue);
  }
  if (questionDef.id === 'propertyCounty') {
    return String(rawValue ?? '');
  }
  return String(rawValue ?? '');
};

const OWEN_BLOCKED_INSIGHT = "That's saved in your LoanKnow file only — I won't use it in my analysis.";

const FIELD_QUESTION_HELP_KEY = '__question__';

const buildOwenTranscript = (msgs, lastQuestionText) => {
  const out = msgs.flatMap((m) => {
    if (m.role === 'user') {
      return [{ role: 'user', text: m.text, questionId: m.questionId }];
    }
    if (m.role === 'assistant' && (m.id === '__welcome__' || m.id === '__owen_reaction__')) {
      return [{ role: 'assistant', text: m.text }];
    }
    return [];
  });
  if (lastQuestionText && out.length > 0 && out[out.length - 1]?.role === 'user') {
    out.splice(out.length - 1, 0, { role: 'assistant', text: lastQuestionText });
  }
  return out;
};

const ConversationalApplyFlow = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const useV1 = LK_FEATURES.v1ProductMatrix;

  const {
    productRecommendation,
    recommendedProducts,
    recommendedProduct,
    readinessScore,
    isAuthenticated,
    currentStep,
    v1,
    guestApplicationId,
    guestSaveEmail,
    lastSaved,
  } = useSelector((state) => state.loanApplication);

  const [messages, setMessages] = useState([]);
  const [owenInsight, setOwenInsight] = useState('');
  const [owenIgnoredLabels, setOwenIgnoredLabels] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showStartOverConfirm, setShowStartOverConfirm] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [chooseProductIds, setChooseProductIds] = useState([]);
  const [legacyDone, setLegacyDone] = useState(false);

  const [activeQuestionId, setActiveQuestionId] = useState(null);
  const [draftValue, setDraftValue] = useState('');
  const [pendingQuestionText, setPendingQuestionText] = useState('');
  const [welcomePhase, setWelcomePhase] = useState('loading');
  const [questionRevealReady, setQuestionRevealReady] = useState(false);
  const [animatedKeys, setAnimatedKeys] = useState([]);
  const [optionHelp, setOptionHelp] = useState(null);
  const [activeHelpValue, setActiveHelpValue] = useState(null);
  const welcomeDoneRef = useRef(false);
  const threadEndRef = useRef(null);

  useEffect(() => {
    if (currentStep === STEPS.ASSESSMENT) {
      dispatch(recalculateReadiness());
    }
  }, [currentStep, dispatch]);

  const assessmentUserAnswerCount = useMemo(
    () => messages.filter((m) => m.role === 'user' && m.questionId).length,
    [messages],
  );

  const displayReadinessScore = assessmentUserAnswerCount === 0 ? 0 : readinessScore;

  const scrollToEnd = useCallback((behavior = 'smooth') => {
    requestAnimationFrame(() => {
      threadEndRef.current?.scrollIntoView({ behavior, block: 'end', inline: 'nearest' });
    });
  }, []);

  const triageQuestions = useMemo(
    () => (useV1 ? getVisibleV1TriageQuestions(v1.triageAnswers) : []),
    [useV1, v1.triageAnswers],
  );

  const fieldQuestions = useMemo(
    () => (useV1 && v1.productId ? getVisibleV1ProductFields(v1.productId, v1.fieldAnswers) : []),
    [useV1, v1.productId, v1.fieldAnswers],
  );

  const product = useV1 ? getV1Product(v1.productId) : null;

  const activeQuestionDef = useMemo(() => {
    if (!activeQuestionId) return null;
    if (useV1) {
      if (v1.phase === 'choose' || activeQuestionId === '__choose_product__') {
        return {
          id: '__choose_product__',
          question: 'Which preparation path fits you best?',
          type: 'radio',
          options: listV1Products(chooseProductIds).map((p) => ({
            value: p.id,
            label: p.name,
          })),
        };
      }
      return (
        triageQuestions.find((q) => q.id === activeQuestionId)
        || fieldQuestions.find((q) => q.id === activeQuestionId)
        || null
      );
    }
    if (activeQuestionId === '__legacy_pick__') {
      return {
        id: '__legacy_pick__',
        question: 'Which loan path should we prepare first?',
        type: 'radio',
        options: (recommendedProducts || []).map((p) => ({
          value: p.id || p.programId,
          label: p.name || p.programName || 'Program',
        })),
      };
    }
    return getLegacyQuestionById(activeQuestionId);
  }, [activeQuestionId, useV1, v1.phase, triageQuestions, fieldQuestions, chooseProductIds, recommendedProducts]);

  useEffect(() => {
    if (welcomePhase === 'loading') return;

    if (activeQuestionId && welcomePhase === 'done' && !legacyDone) {
      const timer = window.setTimeout(() => scrollToEnd(), 60);
      return () => window.clearTimeout(timer);
    }

    if (analyzing || welcomePhase === 'typing') {
      scrollToEnd();
    }
  }, [
    activeQuestionId,
    analyzing,
    legacyDone,
    scrollToEnd,
    welcomePhase,
  ]);

  useEffect(() => {
    if (welcomePhase !== 'done' || activeQuestionId || analyzing) return;
    scrollToEnd();
  }, [messages, activeQuestionId, analyzing, scrollToEnd, welcomePhase]);

  const markAnimated = useCallback((key) => {
    setAnimatedKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
  }, []);

  useEffect(() => {
    setQuestionRevealReady(false);
    setOptionHelp(null);
    setActiveHelpValue(null);
  }, [activeQuestionId, pendingQuestionText]);

  const registerOwenIgnored = useCallback((questionId) => {
    const label = getOwenIgnoreLabelForQuestionId(questionId);
    if (!label) return;
    setOwenIgnoredLabels((prev) => (prev.includes(label) ? prev : [...prev, label]));
  }, []);

  const appendUserAnswer = useCallback((answerLabel, questionId, rawValue) => {
    setMessages((prev) => [
      ...prev,
      { role: 'user', text: answerLabel, questionId, rawValue },
    ]);
  }, []);

  const appendOwenReaction = useCallback((text, { forceThread = false } = {}) => {
    const trimmed = String(text || '').trim();
    if (!trimmed) return;
    setMessages((prev) => {
      if (!forceThread && !shouldMirrorReactionInThread(trimmed, prev)) {
        return prev;
      }
      return [
        ...prev,
        { role: 'assistant', text: trimmed, id: '__owen_reaction__', animate: true },
      ];
    });
  }, []);

  const recomputeOwenIgnoredFromMessages = useCallback((msgs) => {
    const labels = [];
    msgs.forEach((m) => {
      if (m.role !== 'user' || !m.questionId) return;
      const label = getOwenIgnoreLabelForQuestionId(m.questionId);
      if (label && !labels.includes(label)) labels.push(label);
    });
    setOwenIgnoredLabels(labels);
  }, []);

  const clearDownstreamAnswers = useCallback((questionId) => {
    if (!useV1) {
      const visible = getVisibleRecommendationQuestions(productRecommendation);
      const idx = visible.findIndex((q) => q.id === questionId);
      if (idx < 0 && questionId !== '__legacy_pick__') return;
      const toClear = questionId === '__legacy_pick__'
        ? ['__legacy_pick__']
        : visible.slice(idx).map((q) => q.id);
      const patch = {};
      toClear.forEach((id) => { patch[id] = ''; });
      const coreIdx = LEGACY_CORE_QUESTION_ORDER.indexOf(questionId);
      if (coreIdx >= 0) {
        patch.propertyState = '';
        patch.propertyCounty = '';
        patch.propertyZip = '';
        patch.loanSizeTier = '';
        LEGACY_CORE_QUESTION_ORDER.slice(coreIdx).forEach((id) => {
          if (id !== 'propertyLocation' && id !== 'propertyCounty') patch[id] = '';
        });
      }
      dispatch(updateProductRecommendation(patch));
      setLegacyDone(false);
      return;
    }

    if (questionId === '__choose_product__') {
      dispatch(setV1ProductId(null));
      dispatch(setV1Phase('choose'));
      dispatch(clearV1Exit());
      setChooseProductIds(chooseProductIds.length ? chooseProductIds : []);
      return;
    }

    const triageVisible = getVisibleV1TriageQuestions(v1.triageAnswers);
    const triageIdx = triageVisible.findIndex((q) => q.id === questionId);
    if (triageIdx >= 0) {
      const toClear = triageVisible.slice(triageIdx).map((q) => q.id);
      const triagePatch = {};
      toClear.forEach((id) => { triagePatch[id] = ''; });
      dispatch(updateV1TriageAnswers(triagePatch));
      dispatch(setV1Phase('triage'));
      dispatch(setV1ProductId(null));
      dispatch(clearV1Exit());
      setChooseProductIds([]);
      if (v1.productId) {
        const fieldIds = getVisibleV1ProductFields(v1.productId, v1.fieldAnswers).map((f) => f.id);
        const fieldPatch = {};
        fieldIds.forEach((id) => { fieldPatch[id] = ''; });
        if (Object.keys(fieldPatch).length) dispatch(updateV1FieldAnswers(fieldPatch));
      }
      return;
    }

    if (v1.productId && v1.phase === 'fields') {
      const fields = getVisibleV1ProductFields(v1.productId, v1.fieldAnswers);
      const fieldIdx = fields.findIndex((q) => q.id === questionId);
      if (fieldIdx >= 0) {
        const toClear = fields.slice(fieldIdx).map((q) => q.id);
        const fieldPatch = {};
        toClear.forEach((id) => { fieldPatch[id] = ''; });
        dispatch(updateV1FieldAnswers(fieldPatch));
        dispatch(setV1Phase('fields'));
        dispatch(clearV1Exit());
      }
    }
  }, [
    chooseProductIds,
    dispatch,
    productRecommendation,
    useV1,
    v1.fieldAnswers,
    v1.productId,
    v1.triageAnswers,
  ]);

  const handleEditAnswer = useCallback((userMsgIndex) => {
    const userMsg = messages[userMsgIndex];
    if (!userMsg?.questionId || userMsg.role !== 'user') return;

    const questionId = userMsg.questionId;
    const rawValue = userMsg.rawValue ?? userMsg.text;

    setMessages(messages.slice(0, userMsgIndex));
    recomputeOwenIgnoredFromMessages(messages.slice(0, userMsgIndex - 1));
    clearDownstreamAnswers(questionId);

    setActiveQuestionId(questionId);
    if (questionId === 'propertyLocation' && typeof rawValue === 'object') {
      setDraftValue(JSON.stringify({
        ...rawValue,
        mode: rawValue.propertyZip ? 'zip' : 'manual',
      }));
    } else {
      setDraftValue(rawValue);
    }
    setOwenInsight('');
    setAnalyzing(false);
    setLegacyDone(false);
    setPendingQuestionText('');
    setOptionHelp(null);
    setActiveHelpValue(null);
  }, [clearDownstreamAnswers, messages, recomputeOwenIgnoredFromMessages]);

  const loanPurpose = productRecommendation.purchaseOrRefinance;

  const questionTextFor = useCallback((def) => {
    if (!def) return '';
    return getQuestionText(def, loanPurpose);
  }, [loanPurpose]);

  const displayQuestionDef = useMemo(() => {
    if (!activeQuestionDef) return null;
    const withPurpose = (!loanPurpose || activeQuestionDef.id?.startsWith('__'))
      ? activeQuestionDef
      : {
        ...activeQuestionDef,
        question: getQuestionText(activeQuestionDef, loanPurpose),
        placeholder: getQuestionPlaceholder(activeQuestionDef, loanPurpose),
      };
    if (pendingQuestionText) {
      return { ...withPurpose, question: pendingQuestionText };
    }
    return withPurpose;
  }, [activeQuestionDef, loanPurpose, pendingQuestionText]);

  const dismissFieldHelp = useCallback(() => {
    setOptionHelp(null);
    setActiveHelpValue(null);
  }, []);

  const handleFieldHelp = useCallback((optionValue, optionLabel) => {
    const questionDef = displayQuestionDef || activeQuestionDef;
    const helpKey = optionValue ?? FIELD_QUESTION_HELP_KEY;
    if (activeHelpValue === helpKey && optionHelp?.text) {
      dismissFieldHelp();
      return;
    }
    const help = optionValue != null
      ? getOptionHelp({
        questionId: activeQuestionId,
        optionValue,
        optionLabel,
        questionDef,
      })
      : getQuestionHelp({
        questionId: activeQuestionId,
        questionDef,
        loanPurpose,
      });
    setOptionHelp(help);
    setActiveHelpValue(helpKey);
  }, [
    activeHelpValue,
    activeQuestionId,
    activeQuestionDef,
    dismissFieldHelp,
    displayQuestionDef,
    loanPurpose,
    optionHelp?.text,
  ]);

  const syncPropertyInfo = useCallback((patch) => {
    if (!patch.propertyState && !patch.propertyCounty && !patch.propertyZip) return;
    dispatch(updateApplicationData({
      section: 'propertyInfo',
      data: {
        propertyState: patch.propertyState || '',
        propertyCounty: patch.propertyCounty || '',
        propertyZip: patch.propertyZip || '',
      },
    }));
  }, [dispatch]);

  const applyDerivedLoanSizeTier = useCallback((answers) => {
    const tier = deriveLoanSizeTier(answers);
    if (!tier) return answers;
    if (tier === answers.loanSizeTier) return answers;
    dispatch(updateProductRecommendation({ loanSizeTier: tier }));
    return { ...answers, loanSizeTier: tier };
  }, [dispatch]);

  const applyV1NextStep = useCallback((step, nextTriage) => {
    if (!step) return;

    if (step.type === 'exit') {
      dispatch(setV1ExitId(step.id));
      dispatch(setV1Phase('exit'));
      const exit = getV1Exit(step.id);
      setActiveQuestionId(null);
      setMessages((prev) => [...prev, { role: 'assistant', text: exit?.body || 'This path is outside V1 for now.' }]);
      return;
    }

    if (step.type === 'choose_product') {
      setChooseProductIds(step.productIds || []);
      dispatch(setV1Phase('choose'));
      setActiveQuestionId('__choose_product__');
      setDraftValue('');
      return;
    }

    if (step.type === 'product') {
      dispatch(setV1ProductId(step.id));
      dispatch(setV1Phase('fields'));
      const fields = getVisibleV1ProductFields(step.id, {});
      const first = fields[0];
      if (first) {
        setActiveQuestionId(first.id);
        setDraftValue('');
      }
      return;
    }

    if (step.type === 'question') {
      setActiveQuestionId(step.id);
      setDraftValue(nextTriage?.[step.id] || '');
    }
  }, [dispatch]);

  const requestOwenGuidance = useCallback(async ({
    mode,
    answeredQuestionId,
    answeredLabel,
    answers,
    allowedNextSteps,
    transcript,
  }) => {
    setAnalyzing(true);
    setOwenInsight('');
    const programs = useV1
      ? calculateRecommendations(buildLegacyProductRecommendation(v1.triageAnswers, v1.fieldAnswers))
      : calculateRecommendations(answers);

    const guidance = await fetchOwenApplyGuidance({
      mode,
      answeredQuestionId,
      answeredLabel,
      answers,
      readinessScore: displayReadinessScore,
      allowedNextSteps,
      recommendedPrograms: programsForAi(programs.length ? programs : recommendedProducts),
      transcript,
    });

    setOwenInsight(guidance.insight);
    setAnalyzing(false);
    return guidance;
  }, [displayReadinessScore, recommendedProducts, useV1, v1.fieldAnswers, v1.triageAnswers]);

  const startLegacyQuestion = useCallback((questionId, answers = productRecommendation, aiQuestionText = '') => {
    if (questionId === '__legacy_pick__') {
      setPendingQuestionText(aiQuestionText || '');
      setActiveQuestionId('__legacy_pick__');
      setDraftValue('');
      setAnalyzing(false);
      return;
    }
    const q = getLegacyQuestionById(questionId);
    if (!q) return;
    setPendingQuestionText(aiQuestionText || '');
    setActiveQuestionId(questionId);
    setAnalyzing(false);
    if (questionId === 'propertyLocation') {
      setDraftValue(JSON.stringify({
        propertyZip: answers.propertyZip || '',
        propertyState: answers.propertyState || '',
        propertyCounty: answers.propertyCounty || '',
        mode: answers.propertyState && !answers.propertyZip ? 'manual' : 'zip',
      }));
      return;
    }
    setDraftValue(answers[questionId] || '');
  }, [productRecommendation]);

  const finishLegacyAssessment = useCallback((answers = productRecommendation) => {
    const programs = calculateRecommendations(answers);
    dispatch(setRecommendedProducts(programs));

    if (programs.length === 0) {
      setLegacyDone(true);
      appendUserAnswer('No program match yet', '__complete__', '');
      const insight = "I don't have a clear program match yet — we may need a few more details.";
      setOwenInsight(insight);
      appendOwenReaction(insight);
      return;
    }

    if (programs.length === 1) {
      setLegacyDone(true);
      dispatch(setRecommendedProduct(programs[0]));
      const name = programs[0].name || programs[0].programName || 'this program';
      appendUserAnswer(`Prepare ${name}`, '__complete__', programs[0].id || programs[0].programId);
      const insight = `I'd start with ${name} — taking you to the next step.`;
      setOwenInsight(insight);
      appendOwenReaction(insight);
      window.setTimeout(() => {
        if (isAuthenticated) dispatch(setCurrentStep(STEPS.APPLICATION));
        else setShowAccountModal(true);
      }, 1400);
      return;
    }

    setActiveQuestionId('__legacy_pick__');
    setDraftValue('');
    setPendingQuestionText('');
    setAnalyzing(false);
  }, [appendOwenReaction, appendUserAnswer, dispatch, isAuthenticated]);

  const completeSingleProgram = useCallback((answers) => {
    const programs = calculateRecommendations(answers);
    if (programs.length !== 1) return false;
    dispatch(setRecommendedProducts(programs));
    dispatch(setRecommendedProduct(programs[0]));
    setLegacyDone(true);
    setActiveQuestionId(null);
    const name = programs[0].name || programs[0].programName || 'this program';
    const insight = `I'd start with ${name} — taking you to the next step.`;
    setOwenInsight(insight);
    appendOwenReaction(insight, { forceThread: true });
    setAnalyzing(false);
    window.setTimeout(() => {
      if (isAuthenticated) dispatch(setCurrentStep(STEPS.APPLICATION));
      else setShowAccountModal(true);
    }, 1400);
    return true;
  }, [appendOwenReaction, dispatch, isAuthenticated]);

  const applyLegacyNextStep = useCallback((step, answers) => {
    if (!step) {
      finishLegacyAssessment(answers);
      return;
    }
    if (step.type === 'question') {
      startLegacyQuestion(step.id, answers, step.question || '');
      const exists = step.id === '__legacy_pick__' || Boolean(getLegacyQuestionById(step.id));
      if (!exists) {
        const fallbackId = pickNextLegacyQuestionId(answers);
        if (fallbackId) startLegacyQuestion(fallbackId, answers);
        else finishLegacyAssessment(answers);
      }
      return;
    }
    if (step.type === 'complete') {
      if (step.id === 'single_program' && completeSingleProgram(answers)) return;
      finishLegacyAssessment(answers);
      return;
    }
    finishLegacyAssessment(answers);
  }, [completeSingleProgram, finishLegacyAssessment, startLegacyQuestion]);

  const advanceLegacy = useCallback(async (answers, answeredId, answeredLabel, transcript) => {
    const allowed = buildLegacyAllowedNextSteps(answers);
    if (isOwenBlockedQuestionId(answeredId)) {
      setOwenInsight(OWEN_BLOCKED_INSIGHT);
      setAnalyzing(false);
      appendOwenReaction(OWEN_BLOCKED_INSIGHT);
      applyLegacyNextStep(defaultStepFromAllowed(allowed), answers);
      return;
    }
    const { insight, nextStep } = await requestOwenGuidance({
      mode: 'legacy',
      answeredQuestionId: answeredId,
      answeredLabel,
      answers,
      allowedNextSteps: allowed,
      transcript,
    });

    if (insight?.trim()) appendOwenReaction(insight);
    applyLegacyNextStep(nextStep, answers);
  }, [appendOwenReaction, applyLegacyNextStep, requestOwenGuidance]);

  const startV1FieldQuestion = useCallback((productId, fieldAnswers, index = 0) => {
    const fields = getVisibleV1ProductFields(productId, fieldAnswers);
    const q = fields[index];
    if (!q) {
      dispatch(setV1Phase('documents'));
      setActiveQuestionId(null);
      setOwenInsight("You're all set on my questions — your document list is ready.");
      return;
    }
    setActiveQuestionId(q.id);
    setDraftValue(fieldAnswers[q.id] || '');
  }, [dispatch]);

  const goToFirstQuestion = useCallback(() => {
    if (useV1) {
      const first = getVisibleV1TriageQuestions({})[0];
      setActiveQuestionId(first?.id ?? null);
      setDraftValue('');
      return;
    }
    setActiveQuestionId('purchaseOrRefinance');
    setDraftValue('');
  }, [useV1]);

  const beginWelcomeSession = useCallback(async () => {
    welcomeDoneRef.current = false;
    setWelcomePhase('loading');
    setQuestionRevealReady(false);
    setActiveQuestionId(null);
    setDraftValue('');
    setPendingQuestionText('');
    setAnimatedKeys([]);

    const { message } = await fetchOwenApplyWelcome();
    setMessages([{
      role: 'assistant',
      text: message,
      id: '__welcome__',
      animate: true,
    }]);
    setWelcomePhase('typing');
  }, []);

  const handleWelcomeTyped = useCallback(() => {
    if (welcomeDoneRef.current) return;
    welcomeDoneRef.current = true;
    markAnimated('__welcome__');
    setWelcomePhase('done');
    goToFirstQuestion();
  }, [goToFirstQuestion, markAnimated]);

  const bootstrapFirstQuestion = useCallback(() => {
    if (useV1) {
      if (v1.phase === 'triage') {
        goToFirstQuestion();
        return;
      }
      if (v1.phase === 'fields' && v1.productId) {
        startV1FieldQuestion(v1.productId, v1.fieldAnswers, 0);
        return;
      }
      goToFirstQuestion();
      return;
    }
    const firstId = pickNextLegacyQuestionId(productRecommendation);
    if (firstId) startLegacyQuestion(firstId);
  }, [
    useV1,
    v1.phase,
    v1.productId,
    v1.fieldAnswers,
    productRecommendation,
    goToFirstQuestion,
    startLegacyQuestion,
    startV1FieldQuestion,
  ]);

  const handleStartOver = useCallback(() => {
    dispatch(restartApplication());
    setMessages([]);
    setOwenInsight('');
    setOwenIgnoredLabels([]);
    setAnalyzing(false);
    setChooseProductIds([]);
    setLegacyDone(false);
    setPendingQuestionText('');
    setOptionHelp(null);
    setActiveHelpValue(null);
    beginWelcomeSession();
  }, [dispatch, beginWelcomeSession]);

  const openStartOverConfirm = useCallback(() => {
    setShowStartOverConfirm(true);
  }, []);

  const closeStartOverConfirm = useCallback(() => {
    setShowStartOverConfirm(false);
  }, []);

  const confirmStartOver = useCallback(() => {
    setShowStartOverConfirm(false);
    handleStartOver();
  }, [handleStartOver]);

  const openSaveModal = useCallback(() => {
    setSaveError(null);
    setSaveSuccess(false);
    setShowSaveModal(true);
  }, []);

  const closeSaveModal = useCallback(() => {
    if (saving) return;
    setShowSaveModal(false);
    setSaveError(null);
    setSaveSuccess(false);
  }, [saving]);

  const handleSaveApplication = useCallback(async (email) => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const saved = await saveGuestApplication({
        email,
        applicationId: guestApplicationId,
        productRecommendation,
        recommendedProduct,
        recommendedProducts,
        readinessScore,
        currentStep,
        v1,
        messages,
      });
      if (saved?.id) dispatch(setGuestApplicationId(saved.id));
      dispatch(setGuestSaveEmail(String(email).trim().toLowerCase()));
      dispatch(saveProgress());
      setSaveSuccess(true);
    } catch (err) {
      setSaveError(err?.message || 'Could not save your progress. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [
    dispatch,
    guestApplicationId,
    productRecommendation,
    recommendedProduct,
    recommendedProducts,
    readinessScore,
    currentStep,
    v1,
    messages,
  ]);

  const syncLegacyAndProceed = useCallback(() => {
    const legacy = buildLegacyProductRecommendation(v1.triageAnswers, v1.fieldAnswers);
    dispatch(updateProductRecommendation(legacy));
    const rec = buildRecommendedProductFromV1(v1.productId, product);
    dispatch(setRecommendedProduct(rec));

    const nextStep = LK_FEATURES.humanUnderwriter
      ? STEPS.UNDERWRITING_PICKER
      : STEPS.UNDERWRITING;

    if (isAuthenticated) {
      dispatch(setCurrentStep(nextStep));
    } else {
      setShowAccountModal(true);
    }
  }, [dispatch, isAuthenticated, product, v1.fieldAnswers, v1.productId, v1.triageAnswers]);

  const handleAccountCreated = (userData) => {
    dispatch(setUser({
      email: userData.email,
      firstName: userData.firstName,
      lastName: userData.lastName,
      authMethod: 'demo',
    }));
    const nextStep = LK_FEATURES.humanUnderwriter
      ? STEPS.UNDERWRITING_PICKER
      : STEPS.UNDERWRITING;
    window.setTimeout(() => dispatch(setCurrentStep(nextStep)), 100);
  };

  useEffect(() => {
    if (messages.length > 0) return;
    if (useV1 && (v1.phase === 'documents' || v1.phase === 'complete')) return;

    const resumeId = searchParams.get('resume');
    if (!resumeId) {
      ensureFreshApplySession(() => {
        dispatch(resetAssessmentProgress());
      });
    }

    beginWelcomeSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!useV1) return;
    const recs = calculateRecommendations(
      buildLegacyProductRecommendation(v1.triageAnswers, v1.fieldAnswers),
    );
    if (recs.length) dispatch(setRecommendedProducts(recs));
  }, [useV1, v1.triageAnswers, v1.fieldAnswers, dispatch]);

  const handleSubmitAnswer = async (value) => {
    if (!activeQuestionDef || value === '' || value === undefined || value === null) return;
    if (activeQuestionDef.type === 'creditScore' && !isValidCreditScore(value)) return;

    const savedQuestionId = activeQuestionId;
    let label = formatAnswerLabel(activeQuestionDef, value);
    let rawForMessage = value;
    let reduxPatch = {};

    if (savedQuestionId === 'propertyLocation' && typeof value === 'object') {
      reduxPatch = {
        propertyZip: value.propertyZip || '',
        propertyState: value.propertyState || '',
        propertyCounty: value.propertyCounty || '',
      };
      label = formatLocationAnswerLabel(reduxPatch);
      rawForMessage = reduxPatch;
    } else if (savedQuestionId === 'propertyCounty') {
      const hit = lookupByZip(productRecommendation.propertyZip);
      reduxPatch = {
        propertyCounty: value,
        propertyState: hit?.state || productRecommendation.propertyState,
      };
      label = String(value);
    } else {
      reduxPatch = { [savedQuestionId]: value };
    }

    const qText = questionTextFor(displayQuestionDef || activeQuestionDef);
    const newUserMsg = { role: 'user', text: label, questionId: savedQuestionId, rawValue: rawForMessage };
    const transcript = buildOwenTranscript([...messages, newUserMsg], qText);

    appendUserAnswer(label, savedQuestionId, rawForMessage);
    registerOwenIgnored(savedQuestionId);
    setDraftValue(typeof value === 'object' ? JSON.stringify(value) : value);
    setActiveQuestionId(null);
    setOwenInsight('');
    setOptionHelp(null);
    setActiveHelpValue(null);

    if (!useV1) {
      if (savedQuestionId === '__legacy_pick__') {
        const programs = calculateRecommendations(productRecommendation);
        const picked = programs.find((p) => p.id === value || p.programId === value);
        if (picked) {
          dispatch(setRecommendedProduct(picked));
          const insight = `${picked.name || picked.programName} — I'll prepare your file path next.`;
          setOwenInsight(insight);
          appendOwenReaction(insight);
        }
        setLegacyDone(true);
        setAnalyzing(false);
        window.setTimeout(() => {
          if (isAuthenticated) dispatch(setCurrentStep(STEPS.APPLICATION));
          else setShowAccountModal(true);
        }, 600);
        return;
      }

      let nextAnswers = { ...productRecommendation, ...reduxPatch };
      dispatch(updateProductRecommendation(reduxPatch));
      syncPropertyInfo(reduxPatch);
      nextAnswers = applyDerivedLoanSizeTier(nextAnswers);
      await advanceLegacy(nextAnswers, savedQuestionId, label, transcript);
      return;
    }

    if (v1.phase === 'choose' || savedQuestionId === '__choose_product__') {
      dispatch(setV1ProductId(value));
      dispatch(setV1Phase('fields'));
      const p = getV1Product(value);
      const insight = `${p?.name || 'That program'} works — I'll walk you through a few file details next.`;
      setOwenInsight(insight);
      setAnalyzing(false);
      appendOwenReaction(insight);
      startV1FieldQuestion(value, v1.fieldAnswers, 0);
      return;
    }

    if (v1.phase === 'fields') {
      const nextFields = { ...v1.fieldAnswers, [savedQuestionId]: value };
      dispatch(updateV1FieldAnswers({ [savedQuestionId]: value }));
      const allowed = buildV1FieldAllowedNextSteps(v1.productId, nextFields);
      let step;
      if (isOwenBlockedQuestionId(savedQuestionId)) {
        setOwenInsight(OWEN_BLOCKED_INSIGHT);
        setAnalyzing(false);
        appendOwenReaction(OWEN_BLOCKED_INSIGHT);
        step = defaultStepFromAllowed(allowed);
      } else {
        const { insight, nextStep } = await requestOwenGuidance({
          mode: 'v1-field',
          answeredQuestionId: savedQuestionId,
          answeredLabel: label,
          answers: nextFields,
          allowedNextSteps: allowed,
          transcript,
        });
        if (insight?.trim()) appendOwenReaction(insight);
        step = nextStep;
      }

      if (step.type === 'documents') {
        dispatch(setV1Phase('documents'));
        setActiveQuestionId(null);
        return;
      }
      if (step.type === 'question') {
        setActiveQuestionId(step.id);
        setDraftValue(nextFields[step.id] || '');
      }
      return;
    }

    const nextTriage = { ...v1.triageAnswers, [savedQuestionId]: value };
    dispatch(updateV1TriageAnswers({ [savedQuestionId]: value }));
    const allowed = buildV1TriageAllowedNextSteps(nextTriage);
    let step;
    if (isOwenBlockedQuestionId(savedQuestionId)) {
      setOwenInsight(OWEN_BLOCKED_INSIGHT);
      setAnalyzing(false);
      appendOwenReaction(OWEN_BLOCKED_INSIGHT);
      step = defaultStepFromAllowed(allowed);
    } else {
      const { insight, nextStep } = await requestOwenGuidance({
        mode: 'v1-triage',
        answeredQuestionId: savedQuestionId,
        answeredLabel: label,
        answers: nextTriage,
        allowedNextSteps: allowed,
        transcript,
      });
      if (insight?.trim()) appendOwenReaction(insight);
      step = nextStep;
    }
    applyV1NextStep(step, nextTriage);
  };

  if (useV1 && v1.phase === 'documents' && v1.productId) {
    return (
      <div className="lk-conv-apply lk-conv-apply--embedded">
        <div className="lk-conv-apply__frame">
          <header className="lk-conv-apply__header">
            <div className="lk-conv-apply__logo">
              <img src="/assets/loanknow-logo.svg" alt="LoanKnow" />
            </div>
            <button
              type="button"
              className="lk-conv-apply__start-over"
              onClick={openStartOverConfirm}
              aria-label="Start over"
              title="Start over"
            >
              <RotateCcw size={16} />
            </button>
          </header>
          <div style={{ padding: '0 12px 16px' }}>
          <V1DocumentPrep
            productId={v1.productId}
            triageAnswers={v1.triageAnswers}
            fieldAnswers={v1.fieldAnswers}
            onBack={() => {
              dispatch(setV1Phase('fields'));
              startV1FieldQuestion(v1.productId, v1.fieldAnswers, Math.max(0, fieldQuestions.length - 1));
            }}
            onContinue={() => {
              dispatch(setV1Phase('complete'));
              syncLegacyAndProceed();
            }}
          />
          </div>
        </div>
        <StartOverConfirmModal
          open={showStartOverConfirm}
          onClose={closeStartOverConfirm}
          onConfirm={confirmStartOver}
        />
        <AccountCreationModal
          open={showAccountModal}
          onClose={() => setShowAccountModal(false)}
          onAccountCreated={handleAccountCreated}
          savedData={productRecommendation}
        />
      </div>
    );
  }

  const showQuestionZone = activeQuestionDef && !legacyDone && v1.phase !== 'exit' && welcomePhase === 'done';
  const compactChatLayout = showQuestionZone
    && !messages.some((m) => m.role === 'user' && m.questionId);
  const activeQuestionText = questionTextFor(displayQuestionDef || activeQuestionDef);
  const activeQuestionSubtitle = getQuestionSubtitle(displayQuestionDef || activeQuestionDef, loanPurpose);
  const owenSharedEntries = useMemo(
    () => buildOwenSeesFromMessages(messages),
    [messages],
  );
  const applyMilestoneIndex = useMemo(
    () => getActiveApplyMilestoneIndex({
      currentStep,
      v1Phase: v1?.phase,
      hasRecommendedProduct: Boolean(
        recommendedProduct?.id
        || recommendedProduct?.programId
        || v1?.productId
        || (recommendedProducts?.length === 1 && legacyDone),
      ),
    }),
    [
      currentStep,
      v1?.phase,
      v1?.productId,
      recommendedProduct,
      recommendedProducts,
      legacyDone,
    ],
  );
  const activeQuestionAnimKey = activeQuestionId
    ? `q-${activeQuestionId}-${activeQuestionText.slice(0, 32)}`
    : '';

  const renderAssistantMessage = (msg, i) => {
    const animKey = msg.id || `msg-${i}`;
    const shouldAnimate = Boolean(msg.animate) && !animatedKeys.includes(animKey);
    const isWelcome = msg.id === '__welcome__';
    const isOwenNote = msg.id === '__owen_reaction__';

    return (
      <div
        key={`${msg.role}-${i}-${msg.text.slice(0, 24)}`}
        className={`lk-conv-msg lk-conv-msg--assistant${
          isWelcome ? ' lk-conv-msg--welcome' : ''
        }${isOwenNote ? ' lk-conv-msg--owen-note' : ''}`}
      >
        <TypewriterText
          text={msg.text}
          active={shouldAnimate}
          charDelayMs={isWelcome ? 14 : isOwenNote ? 12 : 16}
          onComplete={() => {
            markAnimated(animKey);
            if (isWelcome) handleWelcomeTyped();
          }}
        />
      </div>
    );
  };

  return (
    <div className="lk-conv-apply">
      <div className="lk-conv-apply__frame">
        <header className="lk-conv-apply__header">
          <div className="lk-conv-apply__logo">
            <img src="/assets/loanknow-logo.svg" alt="LoanKnow" />
          </div>
          <div className="lk-conv-apply__header-actions">
            <button
              type="button"
              className="lk-conv-apply__header-btn"
              onClick={openSaveModal}
              aria-label="Save progress"
              title={lastSaved ? 'Save progress (last saved)' : 'Save progress'}
            >
              <Save size={16} />
            </button>
            <button
              type="button"
              className="lk-conv-apply__header-btn"
              onClick={openStartOverConfirm}
              aria-label="Start over"
              title="Start over"
            >
              <RotateCcw size={16} />
            </button>
          </div>
        </header>

        <div className="lk-conv-apply__main">
          <div
            className={`lk-conv-apply__chat${
              compactChatLayout ? ' lk-conv-apply__chat--compact' : ''
            }`}
          >
            <div className="lk-conv-apply__body">
              <div className="lk-conv-apply__thread" role="log" aria-relevant="additions">
                {welcomePhase === 'loading' && <TypingIndicator />}

                {messages.map((msg, i) => {
                  if (msg.role === 'user' && msg.questionId) {
                    return (
                      <button
                        key={`${msg.role}-${i}-${msg.text.slice(0, 24)}`}
                        type="button"
                        className="lk-conv-msg lk-conv-msg--user"
                        onClick={() => handleEditAnswer(i)}
                        title="Change this answer"
                      >
                        {msg.text}
                      </button>
                    );
                  }
                  if (msg.role === 'assistant') {
                    return renderAssistantMessage(msg, i);
                  }
                  return (
                    <div
                      key={`${msg.role}-${i}-${msg.text.slice(0, 24)}`}
                      className={`lk-conv-msg lk-conv-msg--${msg.role}`}
                    >
                      {msg.text}
                    </div>
                  );
                })}

                {analyzing && welcomePhase === 'done' && <TypingIndicator />}
              </div>

              {showQuestionZone && (
                <div className="lk-conv-apply__question-stage">
                  <div className="lk-conv-apply__question-zone">
                    <p className="lk-conv-msg lk-conv-msg--assistant lk-conv-msg--question">
                      <TypewriterText
                        text={activeQuestionText}
                        active={!questionRevealReady}
                        onComplete={() => {
                          markAnimated(activeQuestionAnimKey);
                          setQuestionRevealReady(true);
                        }}
                      />
                    </p>
                    {questionRevealReady && activeQuestionSubtitle && (
                      <p className="lk-conv-apply__question-subtitle">{activeQuestionSubtitle}</p>
                    )}
                    {questionRevealReady && (
                      <div className="lk-conv-apply__inputs">
                        <ConversationalQuestionInput
                          questionDef={displayQuestionDef || activeQuestionDef}
                          value={draftValue}
                          answers={productRecommendation}
                          onChange={setDraftValue}
                          onSubmit={handleSubmitAnswer}
                          onFieldHelp={handleFieldHelp}
                          activeHelpValue={activeHelpValue}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {showQuestionZone && (
                <div className="lk-conv-apply__scroll-spacer" aria-hidden />
              )}

              <div ref={threadEndRef} aria-hidden />
            </div>
          </div>

          <OwenInsightBar
            insight={owenInsight}
            optionHelp={optionHelp}
            onDismissHelp={dismissFieldHelp}
            readinessScore={displayReadinessScore}
            analyzing={analyzing}
            sharedEntries={owenSharedEntries}
            ignoredLabels={owenIgnoredLabels}
            milestoneIndex={applyMilestoneIndex}
          />
        </div>
      </div>

      <SaveApplicationModal
        open={showSaveModal}
        onClose={closeSaveModal}
        initialEmail={guestSaveEmail || ''}
        saving={saving}
        error={saveError}
        success={saveSuccess}
        onSave={handleSaveApplication}
      />

      <StartOverConfirmModal
        open={showStartOverConfirm}
        onClose={closeStartOverConfirm}
        onConfirm={confirmStartOver}
      />

      <AccountCreationModal
        open={showAccountModal}
        onClose={() => setShowAccountModal(false)}
        onAccountCreated={handleAccountCreated}
        savedData={productRecommendation}
      />
    </div>
  );
};

export default ConversationalApplyFlow;
